import {makeAutoObservable} from 'mobx'
import {Lang} from 'yougile-lang'
import {HTMLUtil, logger, Time} from 'yougile-util'
import {
  CustomStickersTypes,
  DataObjectId,
  Hub,
  HubLike,
  noBoardContainerId,
  StickerType,
  TasksListId
} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {PermissionsChecker} from '@client/permissions-checker'
import {Storage} from '@client/storage'
import {Sync} from '@client/sync'
import {UserSettings} from '@client/user-settings'
import {Tracking, TrackingEvent} from '@common/tracking'
import {OptionsItem} from '@components/common/base/dropdown-button-menu/types'
import {MenuSimpleItemData} from '@components/common/base/menu'
import {TableUserInfo} from '@components/common/base/users-table/types'
import {
  ColConfig,
  ListSort,
  Placement,
  SortSource
} from '@components/common/sandbox/list-view/types'
import {UsersTableUtil} from '@components/common/users-table/users-table-util'
import {MessageBox} from '@components/message-box'
import {TasksListsStore} from '@components/my-tasks/tasks-list/tasks-lists-store'
import {
  FiltersStorageKeys,
  getAllUserTasksIdsByType,
  getDefaultSettingsFromStorage
} from '@components/my-tasks/tasks-list/utils'
import {createUseStore} from '@containers/hooks'
import {Board} from '@extensions/board'
import {ChatUtils} from '@extensions/chat-utils'
import {globals} from '@extensions/globals-base'
import {Project} from '@extensions/project'
import {Sticker} from '@extensions/sticker/sticker'
import {Task} from '@extensions/task'
import {getColumns} from './factories/get-columns'

export const DefaultListsId = ['current-tasks', 'done-tasks', 'private-tasks']

interface StickerNameFilter {
  key: DataObjectId | ''
  text: string
  type?: StickerType
}

export interface FilterValues {
  project?: DataObjectId | ''
  board?: DataObjectId
  column?: DataObjectId
  deadline?: [Date, Date]
  author?: DataObjectId[]
  stickers: Map<DataObjectId, (DataObjectId | string)[]>
}

export interface State {
  listId: TasksListId
  isPrivate: boolean
  listTitle: string
  isDeleteListDialogVisible: boolean
  allUserTasks: DataObjectId[]
  openedTaskId?: DataObjectId
  renamingTask?: string
  expanded: boolean
  activeColumns: Set<string>
  filtersExpanded: boolean
  isEditingName: boolean
  isEditListMenuVisible: boolean
  isFirst?: boolean
  isLast?: boolean

  stickersOptions: Map<DataObjectId | '', StickerNameFilter>
  stickerFilterName: (DataObjectId | '')[]
  filtersValues: FilterValues
}

export class TaskListStore {
  state: State
  sort: ListSort
  isDefaultList: boolean
  customOrder: DataObjectId[] // TODO: Probably not needed
  public columns: ColConfig<Hub>[]
  private readonly listsStore: TasksListsStore

  constructor(listId: TasksListId, listsStore: TasksListsStore) {
    this.listsStore = listsStore
    const {panelExpanded, activeColumns, filters} =
      getDefaultSettingsFromStorage(listId)

    const stickerFilterName = Array.from(filters.stickers.keys())

    this.isDefaultList = DefaultListsId.includes(listId)

    this.state = {
      listId,
      isPrivate: UserSettings.getTasksList(listId)?.type === 'private',
      isDeleteListDialogVisible: false,
      listTitle: this.getTaskListTitle(listId),
      allUserTasks: [],
      stickersOptions: new Map(),
      stickerFilterName: stickerFilterName.length ? stickerFilterName : [''],
      expanded: Boolean(panelExpanded),
      filtersExpanded: false,
      activeColumns: activeColumns,
      filtersValues: filters,
      isEditingName: false,
      isEditListMenuVisible: false
    }

    /**
     * Create and cache columns
     */
    this.columns = getColumns()

    /**
     * Initialize custom order from persistent storage
     */
    this.customOrder = this.getCustomOrderFromUserSettings()

    /**
     * Read sort settings from storage for table (back compatibility)
     * and remove it from storage if exists
     * TODO: remove this code after all users will have new sort settings
     * From this...
     */
    const tableSortKey = `${globals.getCompanyId()}-${listId}-sorting`
    const storageSortingForTable = Storage.getItem(tableSortKey)
    if (storageSortingForTable) {
      Storage.removeItem(tableSortKey)
    }
    /* ...to this */

    /**
     * Read sort settings from storage for list
     */
    const storageSorting = Storage.getItem(
      `${globals.getCompanyId()}-${this.state.listId}-list-sorting`
    )
    let deserializationSucceed = false
    if (storageSorting) {
      try {
        this.sort = JSON.parse(storageSorting) as ListSort
        deserializationSucceed = true
      } catch (e) {
        // Sort deserialize error
      }
    }
    if (!deserializationSucceed) {
      /* Fill with defaults */
      this.sort = {
        type: SortSource.ByCustomOrder,
        columnId: null,
        asc: false
      } as ListSort
    }

    makeAutoObservable(this, {}, {autoBind: true})

    if (panelExpanded === undefined && listId === 'current') {
      this.togglePanel()
    }

    this.syncTasks()
  }

  /**
   * Get the title of the current column sort (returns empty string if the custom order is used)
   */
  public getCurrentColumnSortTitle(): string {
    if (this.sort && this.sort.type === SortSource.ByCustomOrder) {
      return ''
    }
    const column = this.columns.find(col => col.id === this.sort?.columnId)
    return column?.sort?.title ?? ''
  }

  syncTasks(): void {
    this.customOrder = this.getCustomOrderFromUserSettings()
    this.state.allUserTasks = getAllUserTasksIdsByType(
      this.state.listId,
      this.state.isPrivate
    )

    this.state.listTitle = this.getTaskListTitle(this.state.listId)

    const box = Sync.getCurrentSandbox()

    for (const taskId of this.state.allUserTasks) {
      const taskHub = Task.getById(taskId)

      if (!taskHub) {
        continue
      }

      if (!Task.getGTDStage(taskHub)) {
        Task.insertInGTD(taskHub, 'new', 'TaskListStore -> syncTasks')
      }

      for (const stickerId of Object.keys(taskHub.stickers) as DataObjectId[]) {
        const stickerItem = Sticker.getById(box, stickerId)
        const stickerType = stickerItem && Sticker.getType(stickerItem)

        if (
          stickerType &&
          !stickerItem?.deleted &&
          !this.state.stickersOptions.has(stickerId) &&
          CustomStickersTypes.includes(stickerType)
        ) {
          this.state.stickersOptions.set(stickerId, {
            key: stickerId,
            text: stickerItem.title,
            type: stickerType
          })
        }
      }
    }
  }

  syncTaskList(): void {
    this.state.listTitle = this.getTaskListTitle(this.state.listId)
  }

  setDeleteListVisible(value: boolean): void {
    this.state.isDeleteListDialogVisible = value
  }

  startChangingListName(): void {
    this.state.isEditingName = true
  }

  stopChangingListName(): void {
    this.state.isEditingName = false
  }

  onChangeListTitle(value: string): void {
    this.state.listTitle = value
  }

  submitNewListTitle(): void {
    if (this.state.listTitle) {
      UserSettings.changeTasksListName(this.state.listTitle, this.state.listId)
    }

    this.state.isEditingName = false
  }

  setEditListMenuVisible(value: boolean): void {
    this.state.isEditListMenuVisible = value
  }

  listMenuOptionClick(id: MenuSimpleItemData['id']): void {
    this.setEditListMenuVisible(false)

    if (id === 'delete') {
      this.setDeleteListVisible(true)
    }
    if (id === 'edit') {
      this.startChangingListName()
    }
  }

  getTaskListTitle(listId: TasksListId): string {
    if (listId === 'current-tasks') {
      return Lang.msg.myTasksIncoming
    }
    if (listId === 'done-tasks') {
      return `${Lang.msg.myTasksDone} ${Lang.msg.taskNoun.few}`
    }

    const tasksList = UserSettings.getTasksList(listId)

    return tasksList?.title || ''
  }

  resetFilters(): void {
    const currentFilterKeys = Object.keys(
      this.state.filtersValues
    ) as (keyof FilterValues)[]

    currentFilterKeys.forEach(filterKey => {
      this.setFilterToStorage(filterKey, undefined)
    })

    this.state.stickerFilterName = ['']
    this.state.filtersValues = {
      stickers: new Map()
    }
  }

  get stickerNamesOptions(): OptionsItem<DataObjectId | string>[] {
    const result: OptionsItem<DataObjectId | ''>[] = [
      {
        key: '',
        text: Lang.msg.stickerFilterName
      }
    ]

    return [
      ...result,
      ...[...this.state.stickersOptions]
        .map(([, value]) => value)
        .filter(option => {
          return !this.state.stickerFilterName.includes(option.key)
        })
    ]
  }

  /**
   * Rename task by id and string value
   * @param taskId
   * @param newName
   */
  renameTask(taskId: DataObjectId, newName: string): void {
    const task = Task.getById(taskId)
    if (!task) {
      return
    }
    const name = HTMLUtil.trimLineBreaks(newName)
    if (name && name !== task.title) {
      const box = Sync.getCurrentSandbox()
      box.editTitle(task, name.trim())
    }
  }

  changeTasksOrderWithinSameList(
    itemId: DataObjectId,
    targetId: DataObjectId,
    before: boolean
  ): void {
    const order = this.getCustomOrderFromUserSettings()
    let itemIndex = order.indexOf(itemId)
    let targetIndex = order.indexOf(targetId)

    let nextOrder = [...order]

    if (targetIndex === -1) {
      /* If target index is not in order, then we need to get all tasks and sort them by the order */
      const allTaskIds = this.filteredTasks
      nextOrder = this.sortTaskIdsByOrder(allTaskIds, order)
      /* Now find the indexes again */
      itemIndex = nextOrder.indexOf(itemId)
      targetIndex = nextOrder.indexOf(targetId)

      if (targetIndex === -1 || itemIndex === -1) {
        /* If target index or item index is still not in order */
        logger.error(
          `Error while sorting tasks by custom order. Task indexes [${[
            itemId,
            targetId
          ].join(',')}] are not found.`
        )
        nextOrder.push(itemId)
        this.setCustomOrder(nextOrder)
        return
      }
    }

    if (itemIndex === -1) {
      // Add task to the list if it's not there
      nextOrder.splice(before ? targetIndex : targetIndex + 1, 0, itemId)
    } else {
      // Move to the new position
      nextOrder.splice(itemIndex, 1)
      if (itemIndex < targetIndex) {
        targetIndex -= 1
      }
      nextOrder.splice(before ? targetIndex : targetIndex + 1, 0, itemId)
    }

    this.setCustomOrder(nextOrder)
  }

  setOpenedTaskId(): void {
    const openChatHub = ChatUtils.getOpenedChatHub()
    this.state.openedTaskId = openChatHub?.id
  }

  togglePanel(): void {
    this.setPanelExpanded(!this.state.expanded)
  }

  setPanelExpanded(expanded: boolean): void {
    if (this.state.expanded === expanded) {
      return
    }
    this.state.expanded = expanded
    const companyId = globals.getCompanyId()

    const prevStorageData =
      (Storage.getItem(`${companyId}-collapsed-tasks-panels`)?.split(
        '.'
      ) as TasksListId[]) || []
    const storageHasKey = prevStorageData.includes(this.state.listId)

    if (
      (!this.state.expanded && storageHasKey) ||
      (this.state.expanded && !storageHasKey)
    ) {
      return
    }

    const newStorageData: TasksListId[] = this.state.expanded
      ? prevStorageData.filter(panel => panel !== this.state.listId)
      : [...prevStorageData, this.state.listId]

    Storage.setItem(
      `${companyId}-collapsed-tasks-panels`,
      newStorageData.join('.')
    )

    /**
     * After collapse or expand we need to update the absolute elements position in other lists.
     * E.g. tippy hints that are always visible
     * Other list can react to this event by observing listsStore.uiRevision
     */
    this.listsStore.triggerUiChange()
  }

  setRenamingTaskId(rowId: string | undefined): void {
    this.state.renamingTask = rowId
  }

  /**
   * Sort task ids by custom order
   * @param taskIds
   * @param order
   * @private
   */
  private sortTaskIdsByOrder(
    taskIds: DataObjectId[],
    order: DataObjectId[]
  ): DataObjectId[] {
    const orderMap = new Map(order.map((id, index) => [id, index]))

    // Сортируем задачи
    return taskIds.sort((a, b) => {
      const indexA = orderMap.get(a) ?? Infinity
      const indexB = orderMap.get(b) ?? Infinity
      return indexA - indexB
    })
  }

  /**
   * Get the list of tasks according to the current filters. The result is cached by MobX
   */
  get filteredTasks(): DataObjectId[] {
    const {allUserTasks, filtersValues} = this.state
    let currentTasks = allUserTasks.reduce<Hub[]>((acc, taskId) => {
      const taskHub = Task.getById(taskId)
      if (taskHub) {
        acc.push(taskHub)
      }
      return acc
    }, [])

    const box = Sync.getCurrentSandbox()

    if (filtersValues.project !== undefined) {
      currentTasks = currentTasks.filter(task => {
        const taskProject = box.getProjectFor(task)

        return filtersValues.project === ''
          ? taskProject?.id === noBoardContainerId
          : taskProject?.id === filtersValues.project
      })
    }

    if (filtersValues.board) {
      currentTasks = currentTasks.filter(task => {
        const taskBoard = Task.getBoardFromTask(task)

        return taskBoard?.id === filtersValues.board
      })
    }

    if (filtersValues.column) {
      currentTasks = currentTasks.filter(task => {
        return task?.location === filtersValues.column
      })
    }

    if (filtersValues.deadline?.[1]) {
      const deadlineValue = filtersValues.deadline

      currentTasks = currentTasks.filter(task => {
        const deadlineSticker = Sticker.getDeadline(task)

        if (!deadlineSticker?.current?.deadline) {
          return false
        }

        return Time.dayBetween(
          deadlineSticker.current.deadline,
          deadlineValue[0].getTime(),
          deadlineValue[1].getTime()
        )
      })
    }

    if (filtersValues.stickers?.size) {
      ;[...filtersValues.stickers].forEach(([stickerId, stickerValue]) => {
        currentTasks = currentTasks.filter(task => {
          if (stickerValue.length === 1) {
            return task.stickers[stickerId] === stickerValue[0]
          }

          return stickerValue.includes(task.stickers[stickerId])
        })
      })
    }

    if (filtersValues.author?.length) {
      currentTasks = currentTasks.filter(task => {
        const author = Task.creator(task)

        return author && filtersValues.author?.includes(author.id)
      })
    }

    return currentTasks.map(task => task.id)
  }

  /**
   * Set the order of tasks in the list
   * It save the order to both the store and the user settings
   * @param order
   */
  setCustomOrder(order: DataObjectId[]): void {
    UserSettings.setOrder(this.state.listId, order)
    this.customOrder = order

    void Tracking.send(
      TrackingEvent.myTasksChangeTheOrderOfTasksInOpenTasksList
    )
  }

  getCustomOrderFromUserSettings(): DataObjectId[] {
    return UserSettings.getOrder(this.state.listId)
  }

  toggleColumnVisibility(columnId: string): void {
    if (this.state.activeColumns.has(columnId)) {
      this.state.activeColumns.delete(columnId)
    } else {
      this.state.activeColumns.add(columnId)
    }

    Storage.setItem(
      `${globals.getCompanyId()}-${this.state.listId}-user-tasks-settings`,
      [...this.state.activeColumns].join('.')
    )
  }

  toggleFilters(): void {
    this.state.filtersExpanded = !this.state.filtersExpanded
  }

  get projectFilterOptions(): OptionsItem<DataObjectId | string | null>[] {
    const box = Sync.getCurrentSandbox()
    return [
      {
        key: null,
        text: Lang.msg.allProjects
      },
      {
        key: '',
        text: Lang.msg.statsFilterWithoutBoard
      },
      ...Project.listProjects(box.companyId, 'not-archive', true)
        .reduce<OptionsItem<DataObjectId>[]>((acc, project) => {
          const boards = Project.listBoards(project)

          if (boards.length) {
            acc.push({
              key: project.id,
              text: project.title,
              order: Project.projectOrder(box.companyId, project.id)
            })
          }
          return acc
        }, [])
        .sort((a, b) => (a?.order || 0) - (b?.order || 0))
    ]
  }

  get boardFilterOptions(): OptionsItem<DataObjectId | ''>[] {
    const projectValue = this.state.filtersValues.project

    const defaultOptions: OptionsItem<DataObjectId | ''>[] = [
      {
        key: '',
        text: Lang.msg.statsFilterAnyBoard
      }
    ]

    if (!projectValue) {
      return defaultOptions
    }
    const projectHub = Sync.getCurrentSandbox().getHub(projectValue)

    return projectHub
      ? [
          ...defaultOptions,
          ...Project.listBoards(projectHub).map(board => {
            return {
              key: board.id,
              text: board.title
            }
          })
        ]
      : defaultOptions
  }

  get columnFilterOptions(): OptionsItem<DataObjectId | ''>[] {
    const boardValue = this.state.filtersValues.board

    const defaultOptions: OptionsItem<DataObjectId | ''>[] = [
      {
        key: '',
        text: Lang.msg.allColumns
      }
    ]

    if (!boardValue) {
      return defaultOptions
    }
    const boardHub = Sync.getCurrentSandbox().getHub(boardValue)
    if (!boardHub) {
      return defaultOptions
    }
    const columns = Board.listColumns(boardHub)

    return [
      ...defaultOptions,
      ...columns.map(column => ({
        key: column.id,
        text: column.title
      }))
    ]
  }

  setStickerFilter(
    stickerId: DataObjectId,
    value: (DataObjectId | string)[]
  ): void {
    if (!value.length && this.state.filtersValues.stickers.has(stickerId)) {
      this.state.filtersValues.stickers.delete(stickerId)
    } else {
      this.state.filtersValues.stickers.set(stickerId, value)
    }

    void Tracking.send(TrackingEvent.myTasksSettingAnyFilterInTheOpenTasksList)

    this.setFilterToStorage(
      'stickers',
      JSON.stringify(Object.fromEntries(this.state.filtersValues.stickers))
    )
  }

  getStickerFilterValue(
    stickerId: DataObjectId | '',
    stickerType?: StickerType
  ): string {
    const value = stickerId
      ? this.state.filtersValues.stickers.get(stickerId)
      : undefined

    if (stickerType && ['text', 'numeric'].includes(stickerType)) {
      return value?.[0] || ''
    }

    return Lang.msg.stickerStates2
  }

  get authorFilterOptions(): TableUserInfo[] {
    const box = Sync.getCurrentSandbox()
    return box.getAllUsers(false).map(x => UsersTableUtil.convertUser(box, x))
  }

  get projectFilterValue(): string | undefined {
    const value =
      this.state.filtersValues.project === undefined
        ? null
        : this.state.filtersValues.project

    return (
      this.projectFilterOptions.find(option => option.key === value)?.text ||
      undefined
    )
  }

  get boardFilterValue(): string | undefined {
    const value = this.state.filtersValues.board || ''
    return (
      this.boardFilterOptions.find(option => option.key === value)?.text ||
      undefined
    )
  }

  get columnFilterValue(): string | undefined {
    const value = this.state.filtersValues.column || ''
    return (
      this.columnFilterOptions.find(option => option.key === value)?.text ||
      undefined
    )
  }

  get deadlineFilterValue(): string {
    const values = this.state.filtersValues.deadline

    if (!values) {
      return Lang.msg.statsCompleteAny
    }

    return values[0].getTime() === values[1].getTime()
      ? Time.formatDate(+values[0])
      : `${Time.formatDate(+values[0])}-${Time.formatDate(+values[1])}`
  }

  get authorFilterValue(): string {
    const users = this.state.filtersValues.author

    if (!users?.length) {
      return Lang.msg.all
    }

    if (users.length === 1) {
      return Accounts.get(users[0], true).realName
    }

    return `${Lang.msg.component.dropdown.selected} ${users.length} ${Lang.msg.component.dropdown.outOf} ${this.authorFilterOptions.length}`
  }

  /**
   * Determines the number of applied filters
   */
  get appliedFiltersAmount(): number {
    const keys = Object.keys(
      this.state.filtersValues
    ) as (keyof State['filtersValues'])[]

    return keys.reduce<number>((acc, filterKey) => {
      if (filterKey === 'stickers') {
        return (
          acc +
          ([...this.state.filtersValues.stickers].filter(
            ([, stickerValue]) => !!stickerValue
          ).length || 0)
        )
      }

      return this.state.filtersValues[filterKey] !== undefined ? acc + 1 : acc
    }, 0)
  }

  public setTaskDone(taskData: HubLike, value: boolean): void {
    const task = Task.getById(taskData.id)
    if (!task) {
      return
    }

    if (!PermissionsChecker.checkComplete(task)) {
      MessageBox.warn(Lang.msg.permissionsFobiddenTaskComplete)
      return
    }
    if (
      PermissionsChecker.shouldFallbackToOldCheckComplete(task) &&
      !PermissionsChecker.oldCheckComplete(task)
    ) {
      MessageBox.warn(Lang.msg.permissionsFobiddenTaskComplete)
      return
    }

    /**
     * Do no send tracking event here, because it's already sent in the Task.changeCompleted method
     */
    /*
    TODO: Remove this block after ensuring that the tracking event is sent in the Task.changeCompleted method
    if (value) {
      if (this.state.isPrivate) {
        Tracking.send(TrackingEvent.myTasksCompletePrivateTaskViaMyTaskPage)
      } else {
        Tracking.send(TrackingEvent.myTasksCompleteTaskViaMyTaskSection)
      }
    }
    */

    Task.changeCompleted(taskData, value, 'my-tasks')
  }

  /**
   * This method is called when the user changes the sorting of the list (not order, sorting)
   * @param sort
   */
  onSortChange(sort: ListSort): void {
    const storageKey = `${globals.getCompanyId()}-${
      this.state.listId
    }-list-sorting`
    this.sort = sort

    Storage.setItem(storageKey, JSON.stringify(sort))
  }

  resetSort(): void {
    this.sort = {
      type: SortSource.ByCustomOrder,
      columnId: null,
      asc: false
    } as ListSort
    Storage.removeItem(
      `${globals.getCompanyId()}-${this.state.listId}-list-sorting`
    )
  }

  setFilterToStorage(
    filterKey: keyof FilterValues,
    filterValue?: string
  ): void {
    const storageKey = `${globals.getCompanyId()}-${this.state.listId}-${
      FiltersStorageKeys[filterKey]
    }`

    if (filterValue !== undefined) {
      Storage.setItem(storageKey, filterValue)
    } else {
      Storage.removeItem(storageKey)
    }
  }

  setProjectFilter(option: OptionsItem<DataObjectId | null>): void {
    if (option.key !== null) {
      this.state.filtersValues.project = option.key
    } else if (this.state.filtersValues.project !== undefined) {
      delete this.state.filtersValues.project
    }

    this.setBoardFilter(undefined, false)
    this.setColumnFilter(undefined, false)

    void Tracking.send(TrackingEvent.myTasksSettingAnyFilterInTheOpenTasksList)

    this.setFilterToStorage(
      'project',
      option.key === null ? undefined : option.key
    )
  }

  setBoardFilter(
    option: OptionsItem<DataObjectId | ''> | undefined,
    tracking = true
  ): void {
    this.state.filtersValues.board = option?.key || undefined
    this.setColumnFilter(undefined, false)

    this.setFilterToStorage('board', this.state.filtersValues.board)

    if (tracking) {
      void Tracking.send(
        TrackingEvent.myTasksSettingAnyFilterInTheOpenTasksList
      )
    }
  }

  setColumnFilter(
    option: OptionsItem<DataObjectId | ''> | undefined,
    tracking = true
  ): void {
    this.state.filtersValues.column = option?.key || undefined

    this.setFilterToStorage('column', this.state.filtersValues.column)

    if (tracking) {
      void Tracking.send(
        TrackingEvent.myTasksSettingAnyFilterInTheOpenTasksList
      )
    }
  }

  setDeadlineFilter(range?: [Date, Date]): void {
    this.state.filtersValues.deadline = range
    const storageValue = this.state.filtersValues.deadline
      ? this.state.filtersValues.deadline
          .map(deadline => deadline.getTime().toString())
          .join('.')
      : undefined

    void Tracking.send(TrackingEvent.myTasksSettingAnyFilterInTheOpenTasksList)

    this.setFilterToStorage('deadline', storageValue)
  }

  setAuthorFilter(selected?: DataObjectId[]): void {
    this.state.filtersValues.author = selected?.length ? selected : undefined
    const filterValue = this.state.filtersValues.author

    void Tracking.send(TrackingEvent.myTasksSettingAnyFilterInTheOpenTasksList)

    this.setFilterToStorage(
      'author',
      filterValue !== undefined ? filterValue.join('.') : undefined
    )
  }

  setStickerFilterName(index: number, value: DataObjectId | ''): void {
    if (this.state.stickerFilterName[index] !== undefined) {
      const prevValue = this.state.stickerFilterName[index]
      this.state.stickerFilterName[index] = value
      if (prevValue) {
        this.state.filtersValues.stickers.delete(prevValue)

        this.setFilterToStorage(
          'stickers',
          JSON.stringify(Object.fromEntries(this.state.filtersValues.stickers))
        )
      }
    }
  }

  getStickerFilterNameValue(index: number): string {
    const stickerId = this.state.stickerFilterName[index]

    if (!stickerId) {
      return Lang.msg.stickerFilterName
    }

    return (
      this.state.stickersOptions.get(stickerId)?.text ||
      Lang.msg.stickerFilterName
    )
  }

  addStickerFilterName(): void {
    this.state.stickerFilterName.push('')
  }

  addTask(
    parentId: DataObjectId | string | null,
    newName: string,
    placement: Placement = Placement.Prepend,
    onIdReady?: (id: DataObjectId) => void
  ): void {
    const newTask = Task.createTask(newName, {
      assigned: [globals.currentUserId],
      isPrivate: this.state.isPrivate,
      location: parentId ? (parentId as DataObjectId) : undefined
    })

    if (newTask) {
      onIdReady?.(newTask.id)
      if (this.state.isPrivate) {
        Tracking.send(TrackingEvent.myTasksCreatePrivate)
        void Tracking.send(
          placement === Placement.Prepend
            ? TrackingEvent.myTasksCreatePrivateTaskViaMyTaskPage
            : TrackingEvent.myTasksCreatePrivateTaskAtTheBottomViaMyTaskPage
        )
      } else {
        void Tracking.send(
          placement === Placement.Prepend
            ? TrackingEvent.myTasksCreateTaskViaMyTaskPage
            : TrackingEvent.myTasksCreateTaskAtTheBottomViaMyTaskPage
        )
      }

      Task.insertInGTD(newTask, 'new', 'TaskListStore -> addTask')

      if (!this.isDefaultList) {
        UserSettings.addTaskToCustomList(
          this.state.listId,
          [newTask.id],
          placement === Placement.Prepend
        )
      }

      /**
       * Add new task to the custom order
       */
      if (placement === Placement.Append) {
        this.setCustomOrder([
          ...this.getCustomOrderFromUserSettings(),
          newTask.id
        ])
      } else {
        this.setCustomOrder([
          newTask.id,
          ...this.getCustomOrderFromUserSettings()
        ])
      }
    }
  }

  deleteList(): void {
    UserSettings.deleteTasksList(this.state.listId)
    if (this.state.isPrivate) {
      void Tracking.send(TrackingEvent.myTasksDeletePrivateListViaMyTaskPage)
    } else {
      void Tracking.send(TrackingEvent.myTasksDeleteListViaMyTaskPage)
    }

    this.setDeleteListVisible(false)
  }

  get tasksListAmount(): string {
    const currentTasksAmount = this.filteredTasks.length

    return currentTasksAmount >= this.state.allUserTasks?.length
      ? `${currentTasksAmount}`
      : `${this.filteredTasks.length || 0} ${Lang.msg.of} ${
          this.state.allUserTasks?.length || 0
        }`
  }
}

const [TasksListContext, useTasksListStore] = createUseStore(TaskListStore)
export {TasksListContext, useTasksListStore}
